import {
  agingBucket,
  daysOverdue,
  gestionesOf,
  montoPagado,
  saldoPendiente,
} from './cobranzas';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysUntilPromesa(promesaFecha, today = todayISO()) {
  const a = new Date(`${String(today).slice(0, 10)}T12:00:00`);
  const b = new Date(`${String(promesaFecha).slice(0, 10)}T12:00:00`);
  return Math.round((b - a) / 86400000);
}

function latestPromesa(factura) {
  let latest = null;
  for (const g of gestionesOf(factura)) {
    if (g.tipo !== 'promesa' || !g.promesaFecha) continue;
    const fecha = String(g.promesaFecha).slice(0, 10);
    if (!latest || fecha > latest) latest = fecha;
  }
  return latest;
}

export function computeCobrarPriority(factura, {
  profesional = false,
  clientScore = null,
  today = todayISO(),
} = {}) {
  const saldo = saldoPendiente(factura);
  if (saldo <= 0.009) return null;

  const promesaFecha = latestPromesa(factura);
  const days = daysOverdue(factura);
  const bucket = agingBucket(factura);
  const partial = montoPagado(factura) > 0.009;
  let priority = 0;
  let reason = '';
  let priorityLabel = 'Cobrar';
  let tone = 'amber';

  if (promesaFecha && promesaFecha < today) {
    priority = 1000 + Math.max(days, 0);
    reason = 'Promesa de cobro vencida';
    priorityLabel = 'Urgente';
    tone = 'red';
  } else if (promesaFecha === today) {
    priority = 900;
    reason = 'Promesa de cobro hoy';
    priorityLabel = 'Promesa hoy';
    tone = 'amber';
  } else if (promesaFecha && promesaFecha > today) {
    const inDays = daysUntilPromesa(promesaFecha, today);
    if (inDays > 0 && inDays <= 3) {
      priority = 880 - inDays;
      reason = inDays === 1 ? 'Promesa de cobro mañana' : `Promesa en ${inDays} días`;
      priorityLabel = 'Promesa próxima';
      tone = 'amber';
    }
  }

  if (!priority && bucket && bucket !== 'vigente') {
    priority = 500 + Math.max(days, 0);
    reason = days === 1 ? 'Vencida ayer' : `${days} días vencida`;
    priorityLabel = days > 30 ? 'Muy vencida' : 'Vencida';
    tone = days > 30 ? 'red' : 'amber';
  } else if (!priority && days === 0) {
    priority = 400;
    reason = 'Vence hoy';
    priorityLabel = 'Vence hoy';
    tone = 'sky';
  } else if (!priority && days < 0) {
    const left = Math.abs(days);
    if (left > 3) return null;
    priority = 300 - left;
    reason = left === 1 ? 'Vence mañana' : `Vence en ${left} días`;
    priorityLabel = 'Próximo';
    tone = 'sky';
  } else if (!priority) {
    return null;
  }

  if (profesional) {
    if (clientScore != null && clientScore < 70) {
      priority += (70 - clientScore) * 4;
      if (clientScore < 40) {
        reason = `${reason} · Score bajo (${clientScore})`;
      }
    }
    if (partial) priority += 20;
    const moneda = factura?.moneda || 'ARS';
    if (moneda === 'ARS') {
      priority += Math.min(saldo / 5000, 40);
    }
  }

  return {
    priority,
    reason,
    priorityLabel,
    tone,
    saldo,
    moneda: factura?.moneda || 'ARS',
    clientScore,
    partial,
  };
}

export function compareCobrarPriority(a, b) {
  if (b.priority !== a.priority) return b.priority - a.priority;
  return (b.saldo || 0) - (a.saldo || 0);
}

export function sortHubRowsByPriority(rows, { promesas = [], profesional = false } = {}) {
  const promesaByInvoice = {};
  for (const promesa of promesas) {
    if (!promesa?.invoiceId || !promesa?.promesaFecha) continue;
    promesaByInvoice[promesa.invoiceId] = promesa.promesaFecha;
  }

  return [...rows].sort((rowA, rowB) => {
    const toFactura = (row) => ({
      numero: row.numero,
      razonSocial: row.cliente,
      total: row.total,
      fecha: row.fecha,
      vencimiento: row.vencimiento,
      estadoPago: 'impaga',
      moneda: row.moneda,
      pagos: row.pagado > 0 ? [{ monto: row.pagado }] : [],
      gestiones: promesaByInvoice[row.id]
        ? [{ tipo: 'promesa', promesaFecha: promesaByInvoice[row.id] }]
        : [],
    });

    const scoredA = computeCobrarPriority(toFactura(rowA), {
      profesional,
      clientScore: rowA.score ?? null,
    }) || { priority: 0, saldo: rowA.saldo || 0 };
    const scoredB = computeCobrarPriority(toFactura(rowB), {
      profesional,
      clientScore: rowB.score ?? null,
    }) || { priority: 0, saldo: rowB.saldo || 0 };

    return compareCobrarPriority(scoredA, scoredB);
  });
}
